import { useCallback } from "react";
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { getActiveParticipantsAsync, registerExistingParticipant, getWaivers, toggleParticipantDeactivated, getParticipantDeactivationImpact } from "../../../../services/participantServices/ParticipantService";
import { getAllRegions } from "../../../../services/userServices/UserInfoService";
import { useAuth } from "../../../../firebase/AuthProvider";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ActiveParticipantDTO, GetAllActiveParticipantsRequest } from "../../../../interfaces/Participant";
import ParticipantsFilterDrawer from "./ParticipantElementsFilter";
import { ROLES, ROLE_PRIORITY } from "../../../../Data/SD";
import { EmailModal } from "./Modal/EmailModal";
import { confirmParticipantDeactivation, optimisticallyReactivateParticipant } from "../../../modal/ParticipantDeactivationModal/participantDeactivationService";
import { getTeamBySupervisorPaged } from "../../../../services/userServices/TeamService";
import { TeamUser } from "../../../../interfaces/team/TeamUser";
import Pagination from "../../../ui/Pagination/Pagination";
import { AxiosError } from "axios";
import { hasPolicyPermissionV2 } from "../../../../utilities/common/policyFuntions";
import { POLICYPERMISSIONS } from "../../../../Data/PolicyPermissions";
import Tooltip from "../../../ui/Tooltip/Tooltip";
import { Search, ListFilter, ChevronDown, MoreVertical, Edit2, ArrowRightLeft, Link as LinkIcon, TrendingUp, UserPlus, Loader2 } from "lucide-react";

// --- Highlighter Component ---
const HighlightedText = ({ text, highlight }: { text: string; highlight: string }) => {
    if (!highlight || !highlight.trim()) {
        return <>{text}</>;
    }
    const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return (
        <>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <span key={i} className="bg-purple-100 text-[#7E57C2] font-semibold rounded-sm px-0.5">{part}</span>
                ) : (
                    part
                )
            )}
        </>
    );
};

interface OptionType {
    value: string;
    label: string;
}

interface CaseworkerSection {
    id: string;
    name: string;
    participantCount: number;
    participants: ActiveParticipantDTO[];
    isExpanded: boolean;
    isLoading: boolean;
    roles?: string[];
}

    export const HEADER_ROLE_MAP: Record<string, string> = {
        "program manager": "Program Manager",
        "supervisor": "Supervisor",
        "owner": "Executive Director",
        "sc": "Support Coordinator",
        "assessor": "Assessor",
        "admin": "Admin",
        "developer": "Developer",
        "scrum master": "Scrum Master"
    };




export const ROLE_MAP: Record<string, string> = {
    "program manager": "PM",
    "supervisor": "Supervisor",
    "owner": "Executive Director",
    "sc": "SC",
    "assessor": "Assessor",
    "admin": "Admin",
    "developer": "DEV",
    "scrum master": "SM"
};

const OwnerParticipantsElement: React.FC = () => {
    const [caseworkerSections, setCaseworkerSections] = useState<CaseworkerSection[]>([]);
    const sectionSnapshotRef = useRef(caseworkerSections);
    useLayoutEffect(() => { sectionSnapshotRef.current = caseworkerSections; }, [caseworkerSections]);
    const [loading, setLoading] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [totalActiveParticipants, setTotalActiveParticipants] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalCaseworkers, setTotalCaseworkers] = useState<number>(0);

    // --- Options State ---
    const [includeDeactivated, setIncludeDeactivated] = useState<boolean>(false);
    const [hideEmptyCaseworkers, setHideEmptyCaseworkers] = useState<boolean>(false);

    const BUMP_BUTTON_ALLOWED_FOR_WAIVER_OPTIONS: string[] = ["EPSDT", "OIDD-CC", "OIDD-SW", "OIDD-ROW", "OAAS-ADHC"];

    // persist pageSize like other pages
    useEffect(() => {
        const saved = localStorage.getItem('ownerParticipantsPageSize');
        if (saved) setPageSize(parseInt(saved, 10));
    }, []);

    useEffect(() => {
        localStorage.setItem('ownerParticipantsPageSize', pageSize.toString());
    }, [pageSize]);

    const resetPageSize = (pageNumber: number, newPageSize: number) => {
        setPage(pageNumber);
        setPageSize(newPageSize);
    };

    const { currentUser, currentRoles, currentOrganizationRolePolicies, userAddedInRolePolicy } = useAuth();
    useNavigate();

    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState("");
    const [selectedParticipant, setSelectedParticipant] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [, setIsTransferParticipantModalOpen] = useState<boolean>(false);
    const [, setIsAddParticipantModalOpen] = useState(false);
    const [, setIsEditParticipantModalOpen] = useState(false);
    const [, setEditingParticipantId] = useState<number | null>(null);
    const [, setIsBumpParticipantModalOpen] = useState(false);
    const [, setBumpingParticipant] = useState<ActiveParticipantDTO | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
    const [sortColumn] = useState<string | null>(null);
    const [sortDirection] = useState<"asc" | "desc" | null>(null);

    // Active Filter States
    const [waiverOptions, setWaiverOptions] = useState<OptionType[]>([]);
    const [regionOptions, setRegionOptions] = useState<OptionType[]>([]);
    const [caseworkerOptions, setCaseworkerOptions] = useState<OptionType[]>([]);
    const [selectedWaiverIds, setSelectedWaiverIds] = useState<string[]>([]);
    const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
    const [selectedCaseworkerIds, setSelectedCaseworkerIds] = useState<string[]>([]);
    const [selectedEmailLinked, setSelectedEmailLinked] = useState<string>("");

    // Pending Filter States
    const [pendingWaiverIds, setPendingWaiverIds] = useState<string[]>([]);
    const [pendingRegionId, setPendingRegionId] = useState<number | null>(null);
    const [pendingCaseworkerIds, setPendingCaseworkerIds] = useState<string[]>([]);
    const [pendingEmailLinked, setPendingEmailLinked] = useState<string>("");

    const [participantToggles, setParticipantToggles] = useState<{ [id: string]: boolean }>({});
    const [pendingParticipantIds, setPendingParticipantIds] = useState<Set<string>>(() => new Set());
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [, setSelectedParticipantForTransfer] = useState<ActiveParticipantDTO | null>(null);
    const menuButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
    const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const buildRequestFromFilters = useCallback((caseworkerId: string): GetAllActiveParticipantsRequest => ({
        pageNumber: 1,
        pageSize: 1000,
        searchTeam: debouncedSearchTerm && debouncedSearchTerm.trim().length > 0 ? debouncedSearchTerm.trim() : null,
        waiverTypeIds: selectedWaiverIds.length > 0 ? selectedWaiverIds.map(Number) : null,
        regionId: selectedRegionId ?? null,
        linkedAccount: selectedEmailLinked === "" ? null : selectedEmailLinked === "yes",
        caseworkerIds: [caseworkerId],
        sortColumn,
        sortDirection,
        participantsUnderMe: !currentRoles?.some((role) => role.name === ROLES.OWNER),
        includeDeactivated: includeDeactivated
    } as any), [debouncedSearchTerm, selectedWaiverIds, selectedRegionId, selectedEmailLinked, sortColumn, sortDirection, currentRoles, includeDeactivated]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const handleOpenFilters = () => {
        setPendingWaiverIds(selectedWaiverIds);
        setPendingRegionId(selectedRegionId);
        setPendingCaseworkerIds(selectedCaseworkerIds);
        setPendingEmailLinked(selectedEmailLinked);
        setShowFilters(true);
    };

    const handleApplyFilters = () => {
        setSelectedWaiverIds(pendingWaiverIds);
        setSelectedRegionId(pendingRegionId);
        setSelectedCaseworkerIds(pendingCaseworkerIds);
        setSelectedEmailLinked(pendingEmailLinked);
        setShowFilters(false);
        setPage(1);
    };

    const handleSaveEmail = async () => {
        try {
            if (!currentUser) return;
            const authToken = await currentUser.getIdToken();
            const trimmedEmail = selectedEmail.trim();
            const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

            if (!trimmedEmail) return toast.error("Email is required");
            if (!isValidEmail) return toast.error("Please enter a valid email address.");

            const response = await registerExistingParticipant(authToken, selectedParticipant, trimmedEmail);

            if (response.success) {
                toast.success(response.message);
                const expandedSection = caseworkerSections.find((s) => s.isExpanded);
                if (expandedSection) await fetchParticipantsForCaseworker(expandedSection.id);
            } else {
                toast.error(response.message || "Failed to link email");
            }
            setIsEmailModalOpen(false);
        } catch (err: any) {
            toast.error(err?.message || "Failed to link the email");
        }
    };

    useEffect(() => {
        if (selectedParticipant !== 0) {
            setSelectedEmail("");
            setIsEmailModalOpen(true);
        }
    }, [selectedParticipant]);



    const fetchParticipantsForCaseworker = async (caseworkerId: string) => {
        try {
            if (!currentUser) return;
            setCaseworkerSections((prev) => prev.map((section) => section.id === caseworkerId ? { ...section, isLoading: true } : section));
            const authToken = await currentUser.getIdToken();
            const req = buildRequestFromFilters(caseworkerId);
            const res = await getActiveParticipantsAsync(authToken, req);

            const newToggles: { [id: string]: boolean } = {};
            res.data.items.forEach((p: ActiveParticipantDTO) => newToggles[p.id] = !p.deactivated);
            setParticipantToggles((prev) => ({ ...prev, ...newToggles }));

            setCaseworkerSections((prev) => prev.map((section) => section.id === caseworkerId ? { ...section, participants: res.data.items, participantCount: res.data.totalCount || section.participantCount, isLoading: false } : section));
        } catch (err: any) {
            toast.error(err instanceof AxiosError ? err.response?.data.message : err.message || "Failed to get participants");
            setCaseworkerSections((prev) => prev.map((section) => section.id === caseworkerId ? { ...section, isLoading: false } : section));
        }
    };

    const toggleSection = async (caseworkerId: string) => {
        const section = caseworkerSections.find((s) => s.id === caseworkerId);
        if (!section) return;

        if (!section.isExpanded && section.participants.length === 0) {
            await fetchParticipantsForCaseworker(caseworkerId);
        }

        setCaseworkerSections((prev) => prev.map((s) => s.id === caseworkerId ? { ...s, isExpanded: !s.isExpanded } : s));
    };

    const handleToggleParticipant = async (participantId: string) => {
        const currentState = participantToggles[participantId];
        if (!currentUser || currentState === undefined || pendingParticipantIds.has(participantId)) return;

        if (currentState) {
            await confirmParticipantDeactivation({
                participantId,
                loadImpact: async () => {
                    const authToken = await currentUser.getIdToken();
                    return getParticipantDeactivationImpact(participantId, authToken);
                },
                deactivate: async (openTaskCount) => {
                    const authToken = await currentUser.getIdToken();
                    return toggleParticipantDeactivated(
                        participantId,
                        authToken,
                        openTaskCount > 0,
                        openTaskCount > 0 ? openTaskCount : undefined,
                        false,
                    );
                },
                setOptimisticActive: (active) => {
                    setParticipantToggles((prev) => ({ ...prev, [participantId]: active }));
                },
                setProcessing: (processing) => {
                    setPendingParticipantIds((previous) => {
                        const next = new Set(previous);
                        if (processing) next.add(participantId);
                        else next.delete(participantId);
                        return next;
                    });
                },
            });
            return;
        }

        await optimisticallyReactivateParticipant({
            reactivate: async () => {
                const authToken = await currentUser.getIdToken();
                return toggleParticipantDeactivated(
                    participantId,
                    authToken,
                    false,
                    undefined,
                    true,
                );
            },
            setOptimisticActive: (active) => {
                setParticipantToggles((prev) => ({ ...prev, [participantId]: active }));
            },
            setProcessing: (processing) => {
                setPendingParticipantIds((previous) => {
                    const next = new Set(previous);
                    if (processing) next.add(participantId);
                    else next.delete(participantId);
                    return next;
                });
            },
        });
    };

    useEffect(() => {
        const fetchFilters = async () => {
            if (!currentUser) return;
            try {
                const authToken = await currentUser.getIdToken();

                const waiversRes = await getWaivers(authToken);
                if (waiversRes && waiversRes.success) setWaiverOptions(waiversRes.data.map((w: any) => ({ value: w.id.toString(), label: w.waiverName })));

                const regionsRes = await getAllRegions(authToken, true);
                if (regionsRes && regionsRes.success) setRegionOptions([{ value: "", label: "All" }, ...regionsRes.data.map((r: any) => ({ value: r.regionId?.toString() ?? String(r.regionId ?? r.id ?? ""), label: r.regionName ?? r.name ?? r.Name ?? "" }))]);

                const usersPaged = await getTeamBySupervisorPaged(authToken, { pageNumber: 1, pageSize: 1000 });
                if (usersPaged && usersPaged.items) setCaseworkerOptions(usersPaged.items.map((u: any) => ({ value: u.id.toString(), label: `${u.firstName} ${u.lastName}` })));
            } catch (err: any) {
                console.error("Error fetching filters:", err);
            }
        };
        fetchFilters();
    }, [currentUser]);

    useEffect(() => {
const fetchCaseworkersWithCounts = async () => {
        const caseworkerSections = sectionSnapshotRef.current;
        try {
            if (!currentUser) return;

            const hasActiveFilters = debouncedSearchTerm.trim().length > 0 || selectedWaiverIds.length > 0 || selectedRegionId !== null || selectedEmailLinked !== "" || selectedCaseworkerIds.length > 0;

            if (caseworkerSections.length === 0) setLoading(true);
            else setIsSearching(true);

            const authToken = await currentUser.getIdToken();
            const fetchAll = hasActiveFilters || selectedCaseworkerIds.length > 0 || hideEmptyCaseworkers;

            const usersPaged = await getTeamBySupervisorPaged(authToken, {
                pageNumber: fetchAll ? 1 : page,
                pageSize: fetchAll ? 1000 : pageSize,
                search: debouncedSearchTerm && debouncedSearchTerm.trim().length > 0 ? debouncedSearchTerm.trim() : undefined,
            });

            let usersRes = usersPaged?.items || [];
            setTotalCaseworkers(usersPaged?.totalCount ?? usersRes.length);

            if (usersRes.length > 0) {
                if (selectedCaseworkerIds.length > 0) {
                    usersRes = usersRes.filter((user: any) => selectedCaseworkerIds.includes(user.id.toString()));
                }

                if (hideEmptyCaseworkers) {
                    usersRes = usersRes.filter((user: any) => (user.participantCount || 0) > 0);
                }

                const total = usersRes.reduce((sum: number, u: TeamUser) => sum + (u.participantCount || 0), 0);
                setTotalActiveParticipants(total);

                const newSections: CaseworkerSection[] = usersRes.map((user: TeamUser) => {
                    const existingSection = caseworkerSections.find(s => s.id === user.id.toString());
                    return {
                        id: user.id.toString(),
                        name: `${user.firstName} ${user.lastName}`,
                        participantCount: user.participantCount || 0,
                        participants: hasActiveFilters ? [] : (existingSection?.participants || []),
                        isExpanded: hasActiveFilters ? false : (existingSection?.isExpanded || false),
                        isLoading: hasActiveFilters,
                        roles: user?.role ? [user.role] : [],
                    };
                });

                if (hasActiveFilters) {
                    const promises = newSections.map(async (section) => {
                        try {
                            const req = buildRequestFromFilters(section.id);
                            const res = await getActiveParticipantsAsync(authToken, req);

                            if (res.data.items.length > 0) {
                                const newToggles: { [id: string]: boolean } = {};
                                res.data.items.forEach((p: ActiveParticipantDTO) => newToggles[p.id] = !p.deactivated);
                                setParticipantToggles((prev) => ({ ...prev, ...newToggles }));

                                return { ...section, participants: res.data.items, participantCount: res.data.totalCount || res.data.items.length, isExpanded: true, isLoading: false };
                            } else {
                                return { ...section, participants: [], participantCount: 0, isExpanded: false, isLoading: false };
                            }
                        } catch (error) {
                            return { ...section, isLoading: false };
                        }
                    });

                    const resolvedSections = await Promise.all(promises);
                    const filteredSections = resolvedSections.filter(section => section.participants.length > 0 || (debouncedSearchTerm && section.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())));

                    setCaseworkerSections(filteredSections);
                    setTotalActiveParticipants(filteredSections.reduce((sum, s) => sum + (s.participantCount || 0), 0));

                } else {
                    const promises = newSections.map(async (section) => {
                        if (section.isExpanded) {
                            try {
                                section.isLoading = true;
                                const req = buildRequestFromFilters(section.id);
                                const res = await getActiveParticipantsAsync(authToken, req);
                                section.participants = res.data.items;
                                section.participantCount = res.data.totalCount ?? section.participantCount;
                                section.isLoading = false;

                                const newToggles: { [id: string]: boolean } = {};
                                res.data.items.forEach((p: ActiveParticipantDTO) => newToggles[p.id] = !p.deactivated);
                                setParticipantToggles((prev) => ({ ...prev, ...newToggles }));
                            } catch (e) {
                                section.isLoading = false;
                            }
                        }
                        return section;
                    });
                    setCaseworkerSections(await Promise.all(promises));
                }
            } else {
                setCaseworkerSections([]);
                setTotalActiveParticipants(0);
            }
        } catch (err: any) {
            toast.error(err instanceof AxiosError ? err.response?.data.message : err.message || "Failed to get participants");
        } finally {
            setLoading(false);
            setIsSearching(false);
        }
    };

        fetchCaseworkersWithCounts();
    }, [currentUser, sortColumn, sortDirection, selectedWaiverIds, selectedRegionId, selectedEmailLinked, debouncedSearchTerm, selectedCaseworkerIds, page, pageSize, includeDeactivated, hideEmptyCaseworkers, buildRequestFromFilters]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm, selectedWaiverIds, selectedRegionId, selectedEmailLinked, selectedCaseworkerIds, sortColumn, sortDirection, hideEmptyCaseworkers]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId !== null) {
                const menuContainer = menuRefs.current[openMenuId];
                const button = menuButtonRefs.current[openMenuId];
                const target = event.target as Node;
                if (menuContainer && button && !menuContainer.contains(target) && !button.contains(target)) {
                    setOpenMenuId(null);
                }
            }
        };
        if (openMenuId !== null) {
            const button = menuButtonRefs.current[openMenuId];
            const menu = menuRefs.current[openMenuId];
            if (button && menu) {
                const rect = button.getBoundingClientRect();
                menu.style.top = `${rect.bottom + 4}px`;
                menu.style.right = `${window.innerWidth - rect.right}px`;
            }
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openMenuId]);

    const linkEmail = (participantId: number) => {
        setSelectedParticipant(participantId);
        setSelectedEmail("");
        setIsEmailModalOpen(true);
    };

    const openTransferParticipantModal = () => {
        const allParticipants = getAllParticipantsForTransfer();
        if (allParticipants.length === 0) return toast.info("Please expand a caseworker section and ensure participants are loaded to transfer");
        setSelectedParticipantForTransfer(null);
        setIsTransferParticipantModalOpen(true);
    };

    const getAllParticipantsForTransfer = (): ActiveParticipantDTO[] => {
        const allParticipants: ActiveParticipantDTO[] = [];
        caseworkerSections.forEach((section) => {
            if (section.isExpanded && section.participants.length > 0) allParticipants.push(...section.participants);
        });
        return allParticipants;
    };

    const getHighestRole = (roles: string[]): string | null => {
        if (!roles || roles.length === 0) return null;
        const highestRawRole = roles.reduce((highest, role) => ROLE_PRIORITY[role] > ROLE_PRIORITY[highest] ? role : highest);
        return ROLE_MAP[highestRawRole.toLowerCase().trim()] ?? highestRawRole;
    };

    const hasDeleteParticiapntPermissionV2 = hasPolicyPermissionV2(currentOrganizationRolePolicies, POLICYPERMISSIONS.Participant.Delete.key, userAddedInRolePolicy);
    const hasEditParticipantPermissionV2 = hasPolicyPermissionV2(currentOrganizationRolePolicies, POLICYPERMISSIONS.Participant.Edit.key, userAddedInRolePolicy) || hasPolicyPermissionV2(currentOrganizationRolePolicies, POLICYPERMISSIONS.Participant.DirectEdit.key, userAddedInRolePolicy);

    return (
        <>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6 flex flex-col h-full w-full">

                {/* --- Top Bar: Search, Filters, Toggles, and Primary Actions --- */}
                <div className="flex flex-col 2xl:flex-row justify-between items-start 2xl:items-center gap-4 mb-6">

                    <div className="flex flex-col xl:flex-row items-start xl:items-center gap-4 w-full 2xl:w-auto">

                        {/* Search and Filter Button */}
                        <div className="flex w-full xl:w-auto gap-3">
                            <div className="relative w-full xl:w-[24rem] 2xl:w-[28rem]">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by name, email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full h-10 pl-10 pr-10 py-2 border border-gray-200 rounded-xl bg-gray-50/50 hover:bg-gray-50 focus:bg-white focus:outline-none focus:border-[#7E57C2] focus:ring-1 focus:ring-[#7E57C2] transition-all text-sm text-gray-800 placeholder:text-gray-400"
                                />
                                {isSearching && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <Spinner animation="border" size="sm" variant="secondary" />
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleOpenFilters}
                                className="flex items-center justify-center gap-2 px-4 h-10 border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 text-gray-700 rounded-xl transition-all text-sm font-medium whitespace-nowrap"
                            >
                                <ListFilter size={16} /> Filters
                            </button>
                        </div>

                        {/* Toggles */}
                        <div className="flex items-center gap-5 px-1">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={hideEmptyCaseworkers}
                                    onChange={(e) => setHideEmptyCaseworkers(e.target.checked)}
                                    className="w-4 h-4 text-[#7E57C2] border-gray-300 rounded focus:ring-[#7E57C2] cursor-pointer"
                                />
                                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors select-none">
                                    Hide empty caseworkers
                                </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={includeDeactivated}
                                    onChange={(e) => setIncludeDeactivated(e.target.checked)}
                                    className="w-4 h-4 text-[#7E57C2] border-gray-300 rounded focus:ring-[#7E57C2] cursor-pointer"
                                />
                                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors select-none">
                                    Include deactivated
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center w-full 2xl:w-auto justify-between 2xl:justify-end gap-4 border-t 2xl:border-t-0 pt-4 2xl:pt-0 border-gray-100">
                        {totalActiveParticipants > 0 && (
                            <span className="text-sm font-medium text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg whitespace-nowrap">
                                {totalActiveParticipants} Participants
                            </span>
                        )}

                        {currentRoles?.some((role) => role.name === ROLES.OWNER || role.name === ROLES.PROGRAM_MANAGER) && (
                            <div className="flex items-center gap-2 ml-auto">
                                <button
                                    onClick={openTransferParticipantModal}
                                    className="flex items-center gap-2 h-10 px-4 rounded-xl bg-[#F9F6FF] hover:bg-[#f0e8ff] text-[#7E57C2] font-medium text-sm transition-colors whitespace-nowrap"
                                >
                                    <ArrowRightLeft size={16} /> Transfer
                                </button>
                                <button
                                    onClick={() => setIsAddParticipantModalOpen(true)}
                                    className="flex items-center gap-2 h-10 px-4 rounded-xl bg-[#7E57C2] hover:bg-[#6b47a8] text-white font-medium text-sm transition-colors shadow-sm hover:shadow whitespace-nowrap"
                                >
                                    <UserPlus size={16} /> Add Participant
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <ParticipantsFilterDrawer
                    show={showFilters}
                    onHide={() => setShowFilters(false)}
                    caseworkerOptions={caseworkerOptions}
                    selectedCaseworkerIds={pendingCaseworkerIds}
                    onCaseworkerChange={setPendingCaseworkerIds}
                    waiverOptions={waiverOptions}
                    selectedWaiverIds={pendingWaiverIds}
                    onWaiverChange={setPendingWaiverIds}
                    regionOptions={regionOptions}
                    selectedRegionId={pendingRegionId}
                    onRegionChange={(val: string) => setPendingRegionId(val === "" ? null : parseInt(val))}
                    selectedEmailLinked={pendingEmailLinked}
                    onEmailLinkedChange={setPendingEmailLinked}
                    onApply={handleApplyFilters}
                    onReset={() => {
                        setSearchTerm("");
                        setPendingCaseworkerIds([]);
                        setPendingWaiverIds([]);
                        setPendingRegionId(null);
                        setPendingEmailLinked("");
                    }}
                />

                {/* --- Main Caseworker List --- */}
                <div className="flex-1 overflow-y-auto pr-1 -mr-1">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-500 gap-3">
                            <Spinner animation="border" variant="secondary" />
                            <span>Loading caseworkers...</span>
                        </div>
                    ) : caseworkerSections.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            {debouncedSearchTerm || selectedWaiverIds.length > 0 || selectedRegionId || selectedEmailLinked || hideEmptyCaseworkers
                                ? "No participants found matching current filters."
                                : "No caseworkers with participants found."}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3 pb-4">
                            {caseworkerSections.map((section) => {
                                const isHighlighted = (debouncedSearchTerm.trim().length > 0 || selectedWaiverIds.length > 0) && section.isExpanded;

                                return (
                                    <div
                                        key={section.id}
                                        className={`bg-white rounded-xl border transition-all duration-300 ease-in-out ${section.isExpanded
                                                ? 'border-[#7E57C2] shadow-sm ring-1 ring-[#7E57C2]/10'
                                                : isHighlighted ? 'border-[#7E57C2] border-l-4' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                            }`}
                                    >
                                        <button
                                            onClick={() => toggleSection(section.id)}
                                            className="w-full flex items-center justify-between p-4 bg-transparent hover:bg-gray-50 transition-colors rounded-xl focus:outline-none"
                                        >
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <h3 className="text-[1.05rem] font-semibold text-gray-900 m-0">
                                                    <HighlightedText text={section.name} highlight={debouncedSearchTerm} />
                                                </h3>
                                                {section.roles && section.roles.length > 0 && (
                                                    <span className="px-2.5 py-0.5 bg-purple-50 text-[#7E57C2] text-xs font-semibold rounded-md border border-purple-100">
                                                        {getHighestRole(section.roles) || section.roles.join(", ")}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <span className="px-3 py-1 text-sm font-medium text-[#7E57C2] bg-[#F9F6FF] rounded-lg hidden sm:block">
                                                    {section.participantCount || 0} Participant{(section.participantCount || 0) !== 1 ? "s" : ""}
                                                </span>
                                                <div className={`p-1 rounded-full transition-transform duration-300 ${section.isExpanded ? 'bg-purple-50 text-[#7E57C2] rotate-180' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}>
                                                    <ChevronDown size={20} />
                                                </div>
                                            </div>
                                        </button>

                                        {/* Expandable Content Area */}
                                        <div className={`grid transition-all duration-300 ease-in-out ${section.isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                            <div className="overflow-visible">
                                                <div className="border-t border-gray-100 bg-gray-50/30">
                                                    {section.isLoading ? (
                                                        <div className="p-8 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                                                            <Spinner animation="border" size="sm" /> Loading participants...
                                                        </div>
                                                    ) : section.participants.length === 0 ? (
                                                        <div className="p-8 text-center text-gray-500 text-sm">
                                                            No participants found matching current filters.
                                                        </div>
                                                    ) : (
                                                        section.participants
                                                            .sort((a, b) => a.lastName.localeCompare(b.lastName))
                                                            .map((participant, index) => (
                                                                <div
                                                                    key={participant.id}
                                                                    className={`p-4 transition-colors hover:bg-white ${index < section.participants.length - 1 ? "border-b border-gray-100" : ""}`}
                                                                >
                                                                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 w-full">

                                                                        <div className="flex items-center gap-4 lg:gap-8 w-full lg:w-auto flex-1 overflow-hidden">
                                                                            {/* Custom Tailwind Toggle */}
                                                                            <Tooltip content={hasDeleteParticiapntPermissionV2 ? '' : "You do not have permission to toggle participants"}>
                                                                                <label className={`relative inline-flex items-center flex-shrink-0 ${hasDeleteParticiapntPermissionV2 && participantToggles[participant.id] !== undefined && !pendingParticipantIds.has(participant.id.toString()) && !participant.activationTransitionInProgress ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={participantToggles[participant.id] ?? !participant.deactivated}
                                                                                        onChange={() => handleToggleParticipant(participant.id.toString())}
                                                                                        disabled={!hasDeleteParticiapntPermissionV2 || participantToggles[participant.id] === undefined || pendingParticipantIds.has(participant.id.toString()) || participant.activationTransitionInProgress}
                                                                                        className="sr-only peer"
                                                                                    />
                                                                                    <div className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${participantToggles[participant.id] ?? true ? 'bg-[#7E57C2]' : 'bg-gray-300'}`}>
                                                                                        {participantToggles[participant.id] === undefined || pendingParticipantIds.has(participant.id.toString()) || participant.activationTransitionInProgress ? (
                                                                                            <Loader2 className="absolute left-3 top-1 h-4 w-4 animate-spin text-white" />
                                                                                        ) : (
                                                                                            <div className={`absolute top-[2px] left-[2px] bg-white w-5 h-5 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${participantToggles[participant.id] ?? true ? 'translate-x-5' : 'translate-x-0'}`} />
                                                                                        )}
                                                                                    </div>
                                                                                </label>
                                                                            </Tooltip>

                                                                            {/* Participant Info Grid */}
                                                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full flex-1">
                                                                                <div className="min-w-0">
                                                                                    <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-gray-400 mb-1">Name</div>
                                                                                    <div className="text-sm font-medium text-gray-900 truncate">
                                                                                        <HighlightedText text={`${participant.firstName} ${participant.lastName}`} highlight={debouncedSearchTerm} />
                                                                                    </div>
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-gray-400 mb-1">Email</div>
                                                                                    <div className="text-sm text-gray-600 truncate">
                                                                                        {participant.email ? <HighlightedText text={participant.email} highlight={debouncedSearchTerm} /> : <span className="text-gray-400 italic">Not provided</span>}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-gray-400 mb-1">Waiver</div>
                                                                                    <div className="text-sm font-medium text-gray-700 truncate">
                                                                                        {participant.waiverName || "-"}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Row Actions */}
                                                                        <div className="flex items-center gap-2 w-full lg:w-auto justify-end flex-shrink-0 mt-2 lg:mt-0">
                                                                            {!participant.email && (
                                                                                <button
                                                                                    onClick={() => linkEmail(participant.id)}
                                                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F9F6FF] text-[#7E57C2] hover:bg-[#7E57C2] hover:text-white rounded-lg text-sm font-medium transition-colors border border-purple-100"
                                                                                >
                                                                                    <LinkIcon size={14} /> Link Account
                                                                                </button>
                                                                            )}

                                                                            {(participant?.waiverName && BUMP_BUTTON_ALLOWED_FOR_WAIVER_OPTIONS.includes(participant.waiverName)) && (
                                                                                <button
                                                                                    onClick={() => { setBumpingParticipant(participant); setIsBumpParticipantModalOpen(true); }}
                                                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors border border-gray-200"
                                                                                >
                                                                                    <TrendingUp size={14} /> Waiver Bump
                                                                                </button>
                                                                            )}

                                                                            {/* Dots Menu */}
                                                                            <div className="relative">
                                                                                <button
                                                                                    ref={(el) => { if (el) menuButtonRefs.current[participant.id.toString()] = el; }}
                                                                                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === participant.id.toString() ? null : participant.id.toString()); }}
                                                                                    className={`p-1.5 rounded-lg transition-colors ${openMenuId === participant.id.toString() ? 'bg-gray-200 text-gray-900' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
                                                                                >
                                                                                    <MoreVertical size={18} />
                                                                                </button>

                                                                                {/* Dropdown Portaled visually using position fixed calculations from ref */}
                                                                                {openMenuId === participant.id.toString() && (
                                                                                    <div
                                                                                        ref={(el) => { if (el) menuRefs.current[participant.id.toString()] = el; }}
                                                                                        className="fixed bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 z-[9999] min-w-[160px] py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100"
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    >
                                                                                        <Tooltip content={!hasEditParticipantPermissionV2 ? "You don't have permission to edit participant" : undefined}>
                                                                                            <button
                                                                                                onClick={(e) => { e.stopPropagation(); setEditingParticipantId(participant.id); setOpenMenuId(null); setIsEditParticipantModalOpen(true); }}
                                                                                                disabled={!hasEditParticipantPermissionV2}
                                                                                                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors text-left ${hasEditParticipantPermissionV2 ? 'text-gray-700 hover:bg-gray-50 hover:text-[#7E57C2]' : 'text-gray-400 cursor-not-allowed'}`}
                                                                                            >
                                                                                                <Edit2 size={16} /> Edit Participant
                                                                                            </button>
                                                                                        </Tooltip>

                                                                                        <button
                                                                                            onClick={() => { setSelectedParticipantForTransfer(participant); setIsTransferParticipantModalOpen(true); setOpenMenuId(null); }}
                                                                                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#7E57C2] transition-colors text-left"
                                                                                        >
                                                                                            <ArrowRightLeft size={16} /> Transfer
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* --- Footer / Pagination --- */}
                <div className="pt-4 border-t border-gray-100 mt-auto">
                    {typeof setPage === 'function' ? (
                        totalCaseworkers > 0 && (
                            <Pagination
                                currentPage={page}
                                totalPages={Math.ceil(totalCaseworkers / pageSize) || 0}
                                onPageChange={(p: number) => setPage(p)}
                                pageSize={pageSize}
                                resetPageSize={resetPageSize}
                            />
                        )
                    ) : (
                        <div className="text-gray-500 text-right text-sm font-medium">
                            Page: {page} | {totalCaseworkers} total caseworkers
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {isEmailModalOpen && (
                <EmailModal isOpen={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)} onSave={handleSaveEmail} email={selectedEmail} setEmail={setSelectedEmail} />
            )}
            {/* <TransferParticipantModal isOpen={isTransferParticipantModalOpen} onClose={() => { setIsTransferParticipantModalOpen(false); setSelectedParticipantForTransfer(null); }} onSuccess={handleParticipantCreated} participant={selectedParticipantForTransfer} participants={selectedParticipantForTransfer ? undefined : getAllParticipantsForTransfer()} />
            <AddParticipantModal isOpen={isAddParticipantModalOpen} onClose={() => setIsAddParticipantModalOpen(false)} onSuccess={handleParticipantCreated} />
            <EditParticipantModal isOpen={isEditParticipantModalOpen} onClose={() => { setIsEditParticipantModalOpen(false); setEditingParticipantId(null); }} onSuccess={handleParticipantCreated} participantId={editingParticipantId} />
            <BumpParticipantModal isOpen={isBumpParticipantModalOpen} onClose={() => { setIsBumpParticipantModalOpen(false); setBumpingParticipant(null); }} onSuccess={handleParticipantCreated} participant={bumpingParticipant} /> */}
        </>
    );
};

export default OwnerParticipantsElement;
